/**
 * Onopvallende banner die verschijnt wanneer de sessie-cookie nog
 * 1–3 dagen geldig is. Geeft de gebruiker proactief een
 * "Vernieuw"-actie zodat ze niet midden in een bewerk-sessie uitgelogd
 * worden.
 *
 * Implementatie:
 * - Server zet bij /auth/verify een niet-httpOnly `ae_sess_exp`-cookie
 *   met de unix-timestamp (seconden) van de vervaldatum.
 * - Banner leest die cookie elke minuut én bij mount, en toont zichzelf
 *   wanneer 1d ≤ remaining ≤ 3d.
 * - "Vernieuw" navigeert naar `/login?next=<currentpath>` zodat de
 *   gebruiker terugkomt op dezelfde pagina na re-login.
 * - "Niet nu" verbergt de banner voor de huidige sessie (sessionStorage)
 *   zodat 'ie niet elke minuut weer verschijnt na dismiss.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const WARN_WINDOW_DAYS_MIN = 1;
const WARN_WINDOW_DAYS_MAX = 3;
const COOKIE_NAME = "ae_sess_exp";
const DISMISS_KEY = "ae.sessionBanner.dismissed";

function readExpCookie(): number | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const value = match.slice(COOKIE_NAME.length + 1);
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readDismissedExp(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.sessionStorage.getItem(DISMISS_KEY);
    if (!v) return null;
    const n = Number.parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function setDismissedExp(exp: number): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DISMISS_KEY, String(exp));
  } catch {
    // private mode; banner blijft komen, niet kritiek
  }
}

export function SessionExpiringBanner() {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Tick elke minuut. Geen seconden-precisie nodig — een dag-grens
  // schuift toch maar één keer per minuut zichtbaar.
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const exp = readExpCookie();
  if (!exp) return null;
  const secondsLeft = exp - now;
  const daysLeft = secondsLeft / 86400;
  if (daysLeft < WARN_WINDOW_DAYS_MIN || daysLeft > WARN_WINDOW_DAYS_MAX) return null;

  // Per-cookie-exp-snapshot dismissed: anders zou een dismiss tot in
  // de volgende sessie blijven gelden. We slaan het exp-tijdstip op
  // bij dismiss; volgende exp (na verlenging) heeft een ander getal,
  // dus banner komt weer terug bij <3d.
  if (readDismissedExp() === exp) return null;

  const renew = () => {
    const next = encodeURIComponent(location.pathname + location.search);
    navigate(`/login?next=${next}`);
  };
  const dismiss = () => {
    setDismissedExp(exp);
    // Force re-render om banner te verwijderen.
    setNow((n) => n);
  };

  // Mens-leesbare "verloopt over X" — afronden naar dichtstbijzijnde
  // halve dag.
  const halves = Math.round(daysLeft * 2) / 2;
  const expiresIn =
    halves === 1
      ? t("auth.sessionExpiring.oneDay")
      : halves === 0.5
        ? t("auth.sessionExpiring.lessThanADay")
        : t("auth.sessionExpiring.days", { n: halves });

  return (
    <div className="border-amber-300 border-b bg-amber-50 px-4 py-2 text-amber-900 text-xs">
      <div className="mx-auto flex max-w-5xl items-center gap-3">
        <span>
          {t("auth.sessionExpiring.messageBefore")}
          <strong>{expiresIn}</strong>
          {t("auth.sessionExpiring.messageAfter")}
        </span>
        <button
          type="button"
          className="ml-auto rounded bg-amber-600 px-2 py-0.5 font-medium text-white text-xs hover:bg-amber-700"
          onClick={renew}
        >
          {t("auth.sessionExpiring.renew")}
        </button>
        <button
          type="button"
          className="rounded border border-amber-300 px-2 py-0.5 text-amber-900 text-xs hover:bg-amber-100"
          onClick={dismiss}
        >
          {t("auth.sessionExpiring.notNow")}
        </button>
      </div>
    </div>
  );
}
